import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import {
  CREDENTIAL_MAX_BYTES,
  CREDENTIAL_TYPES,
  mapCredential,
  type CredentialDocument,
  type CredentialStatus,
} from '../model/credential';

const BUCKET = 'credentials';
const SELECT = 'id, owner_id, storage_path, filename, status, created_at';

export const credentialRepository = {
  async upload(userId: string, file: File): Promise<Result<void>> {
    return guard(async () => {
      if (file.size > CREDENTIAL_MAX_BYTES) raise('validation', 'File too large');
      if (!(CREDENTIAL_TYPES as readonly string[]).includes(file.type)) {
        raise('validation', 'Unsupported file type');
      }
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${userId}/${Date.now()}-${safe}`;
      const up = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
      if (up.error) raise('server', up.error.message, up.error);

      const { error } = await supabase
        .from('credential_documents')
        .insert({ owner_id: userId, storage_path: path, filename: file.name });
      if (error) throw fromPostgrest(error);
    });
  },

  async listMine(userId: string): Promise<Result<readonly CredentialDocument[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('credential_documents')
        .select(SELECT)
        .eq('owner_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCredential);
    });
  },

  async listPending(): Promise<Result<readonly CredentialDocument[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('credential_documents')
        .select(`${SELECT}, owner:profiles!owner_id(full_name)`)
        .eq('status', 'pending')
        .order('created_at', { ascending: true });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCredential);
    });
  },

  async setStatus(id: string, status: CredentialStatus, reviewerId: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase
        .from('credential_documents')
        .update({ status, reviewed_by: reviewerId })
        .eq('id', id);
      if (error) throw fromPostgrest(error);
    });
  },

  async signedUrl(path: string): Promise<Result<string>> {
    return guard(async () => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60);
      if (error) raise('server', error.message, error);
      return data?.signedUrl ?? '';
    });
  },
};
